import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  MyTimesheetEntityEditor,
  MyTimesheetEntitySelector,
} from "my-timesheet/components";
import {
  entitySettingsTableDef,
  entitySettingsTableDefIsMobile,
} from "my-timesheet/configs/table-defs";
import {
  useMyTimesheetBooleanState,
  useMyTimesheetEntityButtonGroupState,
  useMyTimesheetEntityEditorDataState,
  useMyTimesheetClientsList,
} from "my-timesheet/hooks";
import { OcCard, OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState } from "oc/hooks";

interface MyTimesheetClientSettingsProps {
  isMobile: boolean;
}

export const MyTimesheetClientSettings = ({
  isMobile,
}: MyTimesheetClientSettingsProps) => {
  const { t } = useTranslation();

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const { state: dialogOpenState, setState: setDialogOpenState } =
    useMyTimesheetBooleanState({
      id: "showClientSettingsDialog",
    });

  const tableId = "myTimesheetAdminSettingsClientsTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const entity = "MY_TIMESHEET_CLIENT";
  const validityState = useMyTimesheetEntityButtonGroupState(entity);
  const validity: string | undefined =
    validityState === "all" ? undefined : validityState;
  const validityParam = (validity as unknown) as boolean | undefined;

  const handleTableDataChange = (updatedData: typeof initialTableData) => {
    setTableData(updatedData);
  };

  const {
    data,
    loading,
    error,
    createMySheetClient,
    updateMySheetClient,
  } = useMyTimesheetClientsList({
    page: tableData.page,
    pageSize: tableData.pageSize,
    sort: tableData.sortField,
    sortDirection:
      tableData.sortDirection === "asc" || tableData.sortDirection === "desc"
        ? tableData.sortDirection
        : undefined,
    searchText: tableData.searchText.length >= 3 ? tableData.searchText : "",
    searchField: tableData.searchField,
    validity: validityParam,
  });

  const {
    state: entityData,
    setState: setEntityData,
    resetState: resetEntityDataState,
  } = useMyTimesheetEntityEditorDataState();

  const handleSelectRow = (selectedRow: {
    _id: string;
    name: string;
    description?: string | null;
    color?: string;
    validFrom?: string;
    validTo?: string;
  }) => {
    setDialogOpenState(true);
    setEntityData({
      _id: selectedRow._id,
      name: selectedRow.name,
      description: (selectedRow.description ?? "") as string,
      color: selectedRow.color,
      validFrom: selectedRow.validFrom,
      validTo: selectedRow.validTo,
    });
  };

  const handleRowSelection = (row: {
    _id: string;
    name: string;
    description?: string | null;
    color?: string;
    validFrom?: string;
    validTo?: string;
  }) => {
    if (isMobile) {
      setSelectedRowId((prev) => (prev === row._id ? null : row._id));
    } else {
      handleSelectRow(row);
    }
  };

  const handleNewEntity = () => {
    setDialogOpenState(true);
  };

  const handleCloseDialog = () => {
    setDialogOpenState(false);
    resetEntityDataState();
  };

  interface ClientValues {
    _id?: string;
    name: string;
    description?: string | null;
    color?: string;
    validFrom?: string;
    validTo?: string;
  }

  const stripNullish = <T extends Record<string, any>>(obj: T): Partial<T> =>
    Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
    ) as Partial<T>;

  const handleCreateClient = async (values: ClientValues) => {
    if (values._id) {
      const variables = stripNullish({
        _id: values._id,
        name: values.name,
        color: values.color,
        validFrom: values.validFrom,
        validTo: values.validTo,
        description:
          values.description === undefined || values.description === null
            ? undefined
            : values.description === ""
              ? ""
              : values.description,
      });
      await updateMySheetClient({ variables });
    } else {
      await createMySheetClient({
        variables: {
          name: values.name,
          description: values.description ?? "",
          color: values.color,
          validFrom: values.validFrom,
          validTo: values.validTo,
        },
      });
    }
    handleCloseDialog();
  };

  const tableDef = isMobile
    ? entitySettingsTableDefIsMobile
    : entitySettingsTableDef;

  const filteredTableDef = {
    id: entitySettingsTableDef?.id,
    columns: entitySettingsTableDef?.columns?.filter(
      (column) =>
        !entitySettingsTableDefIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id
        )
    ),
  };

  const list = {
    rows: data?.rows,
    total: data?.total,
  };

  return (
    <>
      <MyTimesheetEntityEditor
        dialogLabel={
          entityData._id
            ? t("MY_TIMESHEET_EDIT_CLIENT")
            : t("MY_TIMESHEET_NEW_CLIENT")
        }
        entity={entity}
        open={dialogOpenState}
        onClose={handleCloseDialog}
        entityData={entityData}
        saveEntity={handleCreateClient}
        isMobile={isMobile}
      />
      <OcCard
        label={t("MY_TIMESHEET_CLIENT")}
        loading={loading}
        error={!!error}
      >
        <Box height="16px" />
        <MyTimesheetEntitySelector
          entity={entity}
          onNewEntity={handleNewEntity}
          isMobile={isMobile}
        />
        <Box height="32px" />
        <OcDataTable
          data={list}
          loading={loading}
          tableDef={tableDef}
          tableData={tableData}
          onSelectRow={handleRowSelection}
          setTableData={handleTableDataChange}
          pagination
          selectedRowId={selectedRowId ?? undefined}
          filteredTableDef={filteredTableDef}
          handleMoreButton={handleSelectRow}
          examinerDasboard={false}
          inlineEdit={false}
          isNotExaminer={false}
          localPaging={false}
          tableId={tableId}
        />
      </OcCard>
    </>
  );
};

export default MyTimesheetClientSettings;
